import { observer } from 'mobx-react-lite';
import { useRef } from 'react';

import { company } from '~/company/Company';
import Modal from '~/components/Modal/Modal';
import { useTurnstileWidget } from '~/hooks/useTurnstileWidget';

import { mainStore } from '~/stores';

import { useAuthFlow } from './hooks/useAuthFlow';
import styles from './ModalAuthV3.module.scss';
import { OtpView } from './views/OtpView';
import { PhoneView } from './views/PhoneView';

/**
 * Props for the ModalAuthV3 component.
 */
interface ModalAuthV3Props {
  /** Controls modal visibility */
  isShow: boolean;
}

/**
 * Authentication modal component (v3) that handles user login/registration flow.
 * Supports phone number verification with OTP and integrates with Turnstile captcha.
 */
const ModalAuthV3 = observer(({ isShow }: ModalAuthV3Props) => {
  const {
    step,
    phone,
    agreement,
    otp,
    isLoading,
    isDeletedUser,
    maxAttemptsThreshold,
    captchaWidgetIdRef,
    handleSubmitPhone,
    requestCode,
    handleSubmitOtp,
    handleChangePhone,
    handleClose,
  } = useAuthFlow();

  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const isCaptchaEnabled = company.isEnabledCaptcha && !mainStore.isRN;

  useTurnstileWidget({
    containerRef: captchaContainerRef,
    widgetIdRef: captchaWidgetIdRef,
    enabled: isCaptchaEnabled,
    siteKey: mainStore.captchaSiteID,
    onSuccess: () => requestCode(),
  });

  const handleOtpChange = (value: string, isComplete: boolean) => {
    otp.setValue(value);
    otp.setIsValid(isComplete);
  };

  const isPhoneSubmitDisabled = maxAttemptsThreshold > 0;
  const isOtpSubmitDisabled = !phone.isValid || !otp.isValid;

  return (
    <Modal isShow={isShow} className={styles.root}>
      <div className={styles.container}>
        {step === 'phone' ? (
          <PhoneView
            phoneNumber={phone.value}
            onPhoneChange={phone.setValue}
            onPhoneValidate={phone.setIsValid}
            agreementChecked={agreement.checked}
            onAgreementChange={agreement.setChecked}
            phoneError={phone.error}
            agreementError={agreement.error && agreement.errorVisible}
            onAgreementErrorClose={agreement.handleErrorClose}
            onSubmit={handleSubmitPhone}
            onBack={handleClose}
            isLoading={isLoading}
            isSubmitDisabled={isPhoneSubmitDisabled}
          />
        ) : (
          <OtpView
            phoneNumber={phone.value}
            otpCode={otp.value}
            onOtpChange={handleOtpChange}
            onSubmit={handleSubmitOtp}
            onChangeNumber={handleChangePhone}
            onClose={handleClose}
            isLoading={isLoading}
            isSubmitDisabled={isOtpSubmitDisabled}
            isDeletedUser={isDeletedUser}
          />
        )}

        {isCaptchaEnabled && (
          <div
            ref={captchaContainerRef}
            className="absolute bottom-[calc(max(env(safe-area-inset-bottom),20px)+76px)] left-1/2 -translate-x-1/2"
          />
        )}
      </div>
    </Modal>
  );
});

export default ModalAuthV3;
